import { EDITOR_BREAKPOINTS } from '@mutiny-pkg/tailwind-parser';

// Build regex patterns from editor breakpoints (excluding 'base' which has no prefix)
const PREFIXED_BREAKPOINTS = EDITOR_BREAKPOINTS.filter((bp) => bp !== 'base');
const TO_CONTAINER_QUERY_REGEX = new RegExp(`\\b(${PREFIXED_BREAKPOINTS.join('|')}):`, 'g');
const TO_MEDIA_QUERY_REGEX = new RegExp(`@(${PREFIXED_BREAKPOINTS.join('|')}):`, 'g');

type TransformOptions = {
  isEditor: boolean | undefined;
};

/**
 * Transforms viewport-based breakpoint classes (lg:) to container query classes (@lg:)
 * for use in the editor preview where we want responsive styles to trigger based on
 * container width rather than viewport width.
 *
 * Only applies the transformation when `isEditor` is true.
 *
 * @example
 * transformToContainerQueries('text-sm lg:text-5xl font-bold', { isEditor: true })
 * // Returns: 'text-sm @lg:text-5xl font-bold'
 *
 * transformToContainerQueries('text-sm lg:text-5xl font-bold', { isEditor: false })
 * // Returns: 'text-sm lg:text-5xl font-bold'
 */
export function transformToContainerQueries(className: string | undefined, { isEditor }: TransformOptions): string {
  if (!className) return '';

  if (!isEditor) return className;

  // Transform breakpoint prefixes to container query prefixes
  // This makes Tailwind v4 use container queries instead of media queries
  return className.replace(TO_CONTAINER_QUERY_REGEX, '@$1:');
}

/**
 * Helper to transform component data's className to use container queries.
 * Use this in the editor renderer to make responsive styles work with
 * the constrained viewport preview.
 */
export function withContainerQueries<T extends { className?: string }>(data: T, options: TransformOptions): T {
  return {
    ...data,
    className: transformToContainerQueries(data.className, options),
  };
}

/**
 * Transforms container query classes (@lg:) back to viewport-based classes (lg:)
 * for production rendering where we want normal responsive behavior.
 *
 * @example
 * transformToMediaQueries('text-sm @lg:text-5xl font-bold')
 * // Returns: 'text-sm lg:text-5xl font-bold'
 */
export function transformToMediaQueries(className: string | undefined): string {
  if (!className) return '';

  return className.replace(TO_MEDIA_QUERY_REGEX, '$1:');
}
